import { useMemo, useState, type FormEvent } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import {
  useAddSupplierPrice,
  useDeleteSupplierItemPrices,
  useSupplierItemSearch,
  useSupplierPriceHistory,
  formatSupplierDate,
  isSupplierItemUnmatched,
  todayIsoDate,
  type SupplierItemRow,
  type SupplierTableRow,
} from '../../api/suppliers';
import { Button } from '../ui/Button';
import { DataTable } from '../ui/DataTable';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { ConfirmModal } from '../ui/ConfirmModal';
import { useToast } from '../../hooks/useToast';
import page from '../../pages/Page.module.css';
import styles from './SuppliersPanel.module.css';

function SupplierEditPriceForm({
  item,
  onClose,
  onSave,
  isPending,
}: {
  item: SupplierItemRow;
  onClose: () => void;
  onSave: (payload: { itemId: number; price: number; effective_date: string }) => void;
  isPending: boolean;
}) {
  const [price, setPrice] = useState(item.price != null ? String(item.price) : '');
  const [effectiveDate, setEffectiveDate] = useState(
    item.effective_date?.slice(0, 10) || todayIsoDate()
  );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const priceNum = Number(price);
        if (!Number.isFinite(priceNum)) return;
        onSave({ itemId: item.item_id, price: priceNum, effective_date: effectiveDate });
      }}
    >
      <p className={page.muted} style={{ marginTop: 0 }}>
        Item: {item.item_name}
      </p>
      <div className={page.row} style={{ marginBottom: 16 }}>
        <Input
          label="Price"
          type="number"
          step="0.01"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          required
        />
        <Input
          label="Effective Date"
          type="date"
          value={effectiveDate}
          onChange={(e) => setEffectiveDate(e.target.value)}
          required
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button type="button" variant="ghost" onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button type="submit" variant="success" disabled={isPending}>
          {isPending ? 'Saving…' : 'Save Price'}
        </Button>
      </div>
    </form>
  );
}

function SearchGroupTable({
  supplierId,
  supplierName,
  items,
  isAdmin,
}: {
  supplierId: number;
  supplierName: string;
  items: SupplierItemRow[];
  isAdmin: boolean;
}) {
  const toast = useToast();
  const addPrice = useAddSupplierPrice(supplierId);
  const deleteItemPrices = useDeleteSupplierItemPrices(supplierId);
  const [historyItem, setHistoryItem] = useState<SupplierItemRow | null>(null);
  const [editingItem, setEditingItem] = useState<SupplierItemRow | null>(null);
  const [pendingDelete, setPendingDelete] = useState<SupplierItemRow | null>(null);

  const { data: priceHistory, isLoading: historyLoading } = useSupplierPriceHistory(
    supplierId,
    historyItem?.item_id ?? null
  );

  const rows = useMemo<SupplierTableRow[]>(
    () => items.map((row) => ({ ...row, pendingImport: false as const })),
    [items]
  );

  const cols = useMemo<ColumnDef<SupplierTableRow>[]>(
    () => [
      {
        accessorKey: 'item_name',
        header: 'Item',
        cell: ({ row }) => (
          <span>
            {row.original.item_name}
            {isSupplierItemUnmatched(row.original) ? (
              <span className={styles.supplierUnmatchedBadge}>Not in Item Master</span>
            ) : null}
          </span>
        ),
      },
      {
        id: 'price',
        header: 'Current Price',
        accessorFn: (r) => r.price ?? null,
        cell: ({ row }) =>
          row.original.price != null
            ? Number(row.original.price).toLocaleString(undefined, { minimumFractionDigits: 2 })
            : '—',
      },
      {
        id: 'effective_date',
        header: 'Effective Date',
        accessorKey: 'effective_date',
        cell: ({ row }) => formatSupplierDate(row.original.effective_date),
      },
      {
        id: 'updated',
        header: 'Updated',
        accessorFn: (r) => r.updated_at ?? '',
        cell: ({ row }) => formatSupplierDate(row.original.updated_at),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {isAdmin ? (
              <Button type="button" variant="ghost" size="sm" onClick={() => setEditingItem(row.original)}>
                Edit Price
              </Button>
            ) : null}
            <Button type="button" variant="ghost" size="sm" onClick={() => setHistoryItem(row.original)}>
              Price History
            </Button>
            {isAdmin ? (
              <Button type="button" variant="danger" size="sm" onClick={() => setPendingDelete(row.original)}>
                Delete
              </Button>
            ) : null}
          </div>
        ),
      },
    ],
    [isAdmin]
  );

  return (
    <section className={styles.searchGroup}>
      <h4 className={styles.searchGroupTitle}>{supplierName}</h4>
      <DataTable
        data={rows}
        columns={cols}
        getRowClassName={(row) =>
          isSupplierItemUnmatched(row as SupplierTableRow) ? styles.supplierUnmatchedRow : undefined
        }
      />

      <Modal
        open={!!editingItem}
        onClose={() => setEditingItem(null)}
        title={editingItem ? `Edit Price — ${editingItem.item_name}` : 'Edit Price'}
        showClose={false}
      >
        {editingItem ? (
          <SupplierEditPriceForm
            key={`${supplierId}-${editingItem.item_id}`}
            item={editingItem}
            onClose={() => setEditingItem(null)}
            isPending={addPrice.isPending}
            onSave={(payload) => {
              void addPrice
                .mutateAsync(payload)
                .then(() => {
                  toast('Price saved');
                  setEditingItem(null);
                })
                .catch((err) => toast(err instanceof Error ? err.message : 'Failed to save price', true));
            }}
          />
        ) : null}
      </Modal>

      <Modal
        open={!!historyItem}
        onClose={() => setHistoryItem(null)}
        title={historyItem ? `Price History — ${historyItem.item_name}` : 'Price History'}
        showClose={false}
      >
        {historyLoading ? <p className={page.muted}>Loading…</p> : null}
        {!historyLoading && priceHistory?.length ? (
          <DataTable
            data={priceHistory}
            columns={[
              {
                header: 'Effective Date',
                accessorKey: 'effective_date',
                cell: ({ row }) => formatSupplierDate(row.original.effective_date),
              },
              {
                header: 'Price',
                accessorKey: 'price',
                meta: { align: 'right' as const },
                cell: ({ row }) =>
                  row.original.price != null
                    ? Number(row.original.price).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })
                    : '—',
              },
              {
                header: 'Updated',
                accessorKey: 'updated_at',
                cell: ({ row }) => formatSupplierDate(row.original.updated_at),
              },
            ]}
            searchKeys={[
              (r) => formatSupplierDate(r.effective_date),
              (r) =>
                r.price != null
                  ? Number(r.price).toLocaleString(undefined, { minimumFractionDigits: 2 })
                  : '',
              (r) => formatSupplierDate(r.updated_at),
            ]}
            searchPlaceholder="Date or price…"
          />
        ) : null}
        {!historyLoading && !priceHistory?.length ? (
          <p className={page.muted}>No price history for this item.</p>
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <Button type="button" variant="ghost" onClick={() => setHistoryItem(null)}>
            Close
          </Button>
        </div>
      </Modal>

      <ConfirmModal
        open={pendingDelete !== null}
        title="Delete Item?"
        confirmLabel="Delete"
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (!pendingDelete) return;
          deleteItemPrices.mutate(pendingDelete.item_id, {
            onSuccess: () => {
              toast('Item removed from supplier price list');
              if (historyItem?.item_id === pendingDelete.item_id) setHistoryItem(null);
              if (editingItem?.item_id === pendingDelete.item_id) setEditingItem(null);
              setPendingDelete(null);
            },
            onError: (err) => toast(err instanceof Error ? err.message : 'Failed to delete', true),
          });
        }}
        isPending={deleteItemPrices.isPending}
      >
        <p style={{ margin: 0 }}>
          Remove &quot;{pendingDelete?.item_name ?? ''}&quot; from {supplierName}&apos;s price list? All price
          history for this item will be deleted.
        </p>
      </ConfirmModal>
    </section>
  );
}

export function SupplierItemSearch({ isAdmin }: { isAdmin: boolean }) {
  const [draft, setDraft] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState<string | null>(null);
  const { data, isFetching, isError, error, isFetched } = useSupplierItemSearch(submittedQuery);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const q = draft.trim();
    if (q.length < 2) {
      setSubmittedQuery(null);
      return;
    }
    setSubmittedQuery(q);
  }

  function onClear() {
    setDraft('');
    setSubmittedQuery(null);
  }

  const groups = data?.groups ?? [];
  const totalItems = groups.reduce((n, g) => n + g.items.length, 0);

  return (
    <div className={styles.searchPanel}>
      <form className={styles.searchForm} onSubmit={onSubmit}>
        <div className={styles.searchField}>
          <Input
            label="Search Items Across Suppliers"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Item name or alias…"
          />
        </div>
        <Button type="submit" variant="primary" disabled={draft.trim().length < 2 || isFetching}>
          {isFetching ? 'Searching…' : 'Search'}
        </Button>
        {submittedQuery ? (
          <Button type="button" variant="ghost" onClick={onClear} disabled={isFetching}>
            Clear
          </Button>
        ) : null}
      </form>

      {submittedQuery && isError ? (
        <p style={{ color: 'var(--color-danger)', marginTop: 12 }}>
          {error instanceof Error ? error.message : 'Search failed'}
        </p>
      ) : null}

      {submittedQuery && isFetched && !isFetching && !isError && totalItems === 0 ? (
        <p className={page.muted} style={{ marginTop: 12 }}>
          No items match &quot;{submittedQuery}&quot; across suppliers.
        </p>
      ) : null}

      {submittedQuery && groups.length > 0 ? (
        <div className={styles.searchResults}>
          <p className={page.muted} style={{ margin: '12px 0 0' }}>
            {totalItems} item{totalItems === 1 ? '' : 's'} across {groups.length} supplier
            {groups.length === 1 ? '' : 's'}
          </p>
          {groups.map((group) => (
            <SearchGroupTable
              key={group.supplier_id}
              supplierId={group.supplier_id}
              supplierName={group.supplier_name}
              items={group.items}
              isAdmin={isAdmin}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
